import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, Input, OnDestroy } from "@angular/core";
import { ForgeIconModule, ForgeListModule } from "@tylertech/forge-angular";

@Component({
    selector: "drag-and-drop-group",
    templateUrl: "./drag-and-drop-group.tmpl.html",
    styleUrl: "./drag-and-drop-group.component.scss",
    imports: [
        CdkDrag,
        CdkDropList,
        ForgeIconModule,
        ForgeListModule,
    ]
})
export class DragAndDropGroupComponent<T extends object = Record<string, unknown>> implements OnDestroy
{
    @Input() readonly title: string;

    @Input() readonly items: Array<T>;

    @Input() readonly displayProperty: keyof T & string;

    screenReaderAnnouncement: string = "";

    private keyboardMoveMode: boolean = false;
    private movingItemIndex: number | null = null;
    private originalItemIndex: number | null = null;
    private announceTimeoutId: ReturnType<typeof setTimeout> | null = null;

    getDisplay(item: T): string
    {
        return item[this.displayProperty]?.toString();
    }

    onCdkDropListDropped(event: CdkDragDrop<Array<T>>): void
    {
        moveItemInArray<T>(this.items, event.previousIndex, event.currentIndex);
    }

    get isKeyboardMoveMode(): boolean
    {
        return this.keyboardMoveMode;
    }

    isMoving(itemIndex: number): boolean
    {
        return this.keyboardMoveMode && this.movingItemIndex === itemIndex;
    }

    getItemAriaLabel(item: T, itemIndex: number): string
    {
        const groupClause = this.title ? ` in ${this.title}` : "";
        return `${this.getDisplay(item)}, item ${itemIndex + 1} of ${this.items.length}${groupClause}. Press Space to move.`;
    }

    onItemKeyDown(event: KeyboardEvent, itemIndex: number): void
    {
        if (event.key === " " || event.key === "Spacebar" || event.key === "Enter")
        {
            event.preventDefault();
            if (!this.keyboardMoveMode)
            {
                this.enterKeyboardMoveMode(itemIndex);
            }
            else
            {
                this.commitMove();
            }
            return;
        }

        if (!this.keyboardMoveMode)
        {
            return;
        }

        if (event.key === "ArrowUp")
        {
            event.preventDefault();
            this.moveItem(-1);
        }
        else if (event.key === "ArrowDown")
        {
            event.preventDefault();
            this.moveItem(1);
        }
        else if (event.key === "Escape")
        {
            event.preventDefault();
            event.stopPropagation();
            this.cancelMove();
        }
    }

    onItemBlur(event: FocusEvent): void
    {
        if (!this.keyboardMoveMode)
        {
            return;
        }

        // Reordering keeps the same DOM node (tracked by item identity), so a
        // reorder does not blur the item. A blur with a concrete new target
        // therefore means focus genuinely left the item, so commit the move.
        // Ignore blurs with no related target (transient focus churn).
        const related = event.relatedTarget as HTMLElement | null;
        if (!related)
        {
            return;
        }

        this.commitMove();
    }

    ngOnDestroy(): void
    {
        if (this.announceTimeoutId !== null)
        {
            clearTimeout(this.announceTimeoutId);
        }
    }

    private enterKeyboardMoveMode(itemIndex: number): void
    {
        this.keyboardMoveMode = true;
        this.movingItemIndex = itemIndex;
        this.originalItemIndex = itemIndex;

        const item = this.items[itemIndex];
        this.announceToScreenReader(
            `Move mode activated for ${this.getDisplay(item)}, position ${itemIndex + 1} of ${this.items.length}. `
            + `Use the up and down arrow keys to move, Space or Enter to drop, Escape to cancel.`
        );
    }

    private moveItem(direction: number): void
    {
        if (this.movingItemIndex === null)
        {
            return;
        }

        const from = this.movingItemIndex;
        const to = from + direction;

        if (to < 0 || to >= this.items.length)
        {
            const boundary = direction < 0 ? "top" : "bottom";
            this.announceToScreenReader(`${this.getDisplay(this.items[from])} is already at the ${boundary} of the list.`);
            return;
        }

        moveItemInArray(this.items, from, to);
        this.movingItemIndex = to;

        const item = this.items[to];
        this.announceToScreenReader(`${this.getDisplay(item)}, position ${to + 1} of ${this.items.length}.`);
        this.focusMovingItemAfterRender(to);
    }

    private commitMove(): void
    {
        if (this.movingItemIndex === null)
        {
            this.exitKeyboardMoveMode();
            return;
        }

        const item = this.items[this.movingItemIndex];
        this.announceToScreenReader(`${this.getDisplay(item)} dropped at position ${this.movingItemIndex + 1} of ${this.items.length}.`);
        this.exitKeyboardMoveMode();
    }

    private cancelMove(): void
    {
        if (this.movingItemIndex === null || this.originalItemIndex === null)
        {
            this.exitKeyboardMoveMode();
            return;
        }

        const originalIndex = this.originalItemIndex;
        moveItemInArray(this.items, this.movingItemIndex, originalIndex);
        this.announceToScreenReader(`Move cancelled. ${this.getDisplay(this.items[originalIndex])} returned to position ${originalIndex + 1}.`);
        this.focusMovingItemAfterRender(originalIndex);
        this.exitKeyboardMoveMode();
    }

    private exitKeyboardMoveMode(): void
    {
        this.keyboardMoveMode = false;
        this.movingItemIndex = null;
        this.originalItemIndex = null;
    }

    private focusMovingItemAfterRender(itemIndex: number): void
    {
        setTimeout(() =>
        {
            const element = document.querySelector(
                `forge-list-item[data-group-title="${CSS.escape(this.title ?? "")}"][data-item-index="${itemIndex}"]`
            ) as HTMLElement | null;
            element?.focus();
        }, 0);
    }

    private announceToScreenReader(message: string): void
    {
        this.screenReaderAnnouncement = message;

        if (this.announceTimeoutId !== null)
        {
            clearTimeout(this.announceTimeoutId);
        }

        this.announceTimeoutId = setTimeout(() =>
        {
            this.screenReaderAnnouncement = "";
            this.announceTimeoutId = null;
        }, 1000);
    }
}
